import { Collapse, List, Button } from 'antd';
import * as React from 'react';
import { observer } from 'mobx-react';

import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { MapView, MapMarker } from 'src/core/ui/map.view';
import { AccommodationCard } from './search-results-accommodation.view';
import { CarRentCard } from 'src/app/bookings/search/results/search-results-car-rent.view';
import { GeneralCard } from 'src/app/bookings/search/results/search-results-general.view';
import { TransferCard } from 'src/app/bookings/search/results/search-results-transfer.view';
import { TourCard } from 'src/app/bookings/search/results/search-results-tour.view';
import { FlightCard } from 'src/app/bookings/search/results/search-results-flight.view';

interface SearchResultProps extends ComponentViewProps<DataSet<ProductSearchResult>> {
	onPagingSearch?: () => Promise<void>;
	count: number;
	typeSearch: SearchType;
	mode: ControlMode;
	yourSearch?: string;
	onProductSelected?: (selection: ResultSelection[], search: Search) => void;
	currentSearch: Search;
	marketId: number;
	bookingDate: Date;
	languageCode: string;
	showSearch?: boolean;
	messages?: DataSet<Message>;
	showAllExpanded: boolean;
	fullSreen: boolean;
	replaceResult?: (oldResult: ProductSearchResult, replacements: ApiResult) => void;
	taxEditor?: React.ReactNode;
	disableAddButton?: (val: boolean) => void;
}

interface SearchResultState {
	selection?: { [unitIndex: string]: ResultSelection };
}

@observer
export class SearchResultView extends React.Component<SearchResultProps, SearchResultState> {

	public state: SearchResultState = {};

	private messageColumn: Column[] = [
		{
			key: 'severity',
			title: 'Severity',
			dataIndex: 'severity',
			render: (text: string, record: Message) => {
				switch (record.severity) {
					case 0:
						return 'Info';
					case 1:
						return 'Warning';
					case 2:
						return 'Error';
				}
				return null;
			}
		},
		{
			key: 'text',
			title: 'Message',
			dataIndex: 'text',
		}
	];

	private groupProducts(items: ProductSearchResult[]): ProductSearchResult[][] {
		const groups = new Map<number | string, ProductSearchResult[]>();
		items.forEach((m) => {
			if (m.product.internalId) {
				let g = groups.get(m.product.internalId);
				if (g == null) {
					groups.set(m.product.internalId, g = []);
				}
				g.push(m);
			} else {
				groups.set(m.product.externalId, [m]);
			}
		});

		return Array.from(groups.values());
	}

	renderItem = (item: ProductSearchResult[]): JSX.Element => {
		const { currentSearch, setBusy, bookingDate, languageCode, marketId, replaceResult } = this.props;
		const { selection } = this.state;

		let node;

		//cambie esto para poder pasarle al Accommodation el otro onChangeSelection
		const commonProps = {
			setBusy,
			selection,
			// onChangeSelection: this.onChangeSelection,
			onChangeOptionalSelection: this.onChangeOptionalSelection,
			children: this.props.children,
			taxEditor: this.props.taxEditor,
			disableAddButton: this.props.disableAddButton,
		};

		switch (currentSearch.type) {
			case 'Accommodation':
				node = (
					<AccommodationCard
						items={item as AccommodationSearchResult[]}
						onChangeSelection={this.onChangeSelection1}
						{...commonProps}
					/>
				);
				break;
			case 'CarRent':
				node = (
					<CarRentCard
						items={item as CarRentSearchResult[]}
						onChangeSelection={this.onChangeSelection}
						{...commonProps}
					/>
				);
				break;
			case 'Flights':
				node = (
					<FlightCard
						items={item as FlightSearchResult[]}
						onChangeSelection={this.onChangeSelection}
						{...commonProps}
					/>
				);
				break;
			case 'General':
				node = (
					<GeneralCard
						items={item as GeneralSearchResult[]}
						onChangeSelection={this.onChangeSelection}
						{...commonProps}
					/>
				);
				break;
			case 'Transfer':
				node = (
					<TransferCard
						items={item as TransferSearchResult[]}
						onChangeSelection={this.onChangeSelection}
						{...commonProps}
					/>
				);
				break;
			case 'Tour':
				node = (
					<TourCard
						search={currentSearch}
						items={item as TourSearchResult[]}
						onChangeSelection={this.onChangeSelection}
						bookingDate={bookingDate}
						languageCode={languageCode}
						marketId={marketId}
						{...commonProps}
					/>
				);
				break;
		}

		return (
			<List.Item key={item[0].product.internalId}>
				{node}
			</List.Item>
		);
	}

	render(): JSX.Element {
		const { model, setBusy, yourSearch, typeSearch, messages, count } = this.props;

		if (model == null) {
			return null;
		}

		let yourSearchLabel = 'Your';
		switch (typeSearch) {
			case 'Accommodation':
				{
					yourSearchLabel = yourSearchLabel.concat(' Accommodation Search: ');
					break;
				}
			case 'CarRent':
				{
					yourSearchLabel = yourSearchLabel.concat(' Car Rent Search: ');
					break;
				}
			case 'Transfer':
				{
					yourSearchLabel = yourSearchLabel.concat(' Transfer Search: ');
					break;
				}
			case 'Flights':
				{
					yourSearchLabel = yourSearchLabel.concat(' Flights Search: ');
					break;
				}
			case 'General':
				{
					yourSearchLabel = yourSearchLabel.concat(' General Search: ');
					break;
				}
			case 'Tour':
				{
					yourSearchLabel = yourSearchLabel.concat(' Tour Search: ');
					break;
				}
		}
		yourSearchLabel = yourSearchLabel.concat(yourSearch);

		const headerView = (!!yourSearch && yourSearch != '')
			? yourSearchLabel
			: undefined;
		const headerMsg = messages && messages.count > 0
			? (
				<Collapse.Panel key="2" header={`Messages (${messages.count})`}>
					<DataTableView
						mode="display"
						setBusy={setBusy}
						hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideFooter | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
						columns={this.messageColumn}
						model={messages}
					/>
				</Collapse.Panel>
			)
			: undefined;
		const loadMore = model.items.length === count || this.props.onPagingSearch == null
			? null
			: <div style={{ textAlign: 'center', marginTop: 12 }}><Button onClick={this.onLoadMore}>Show more</Button></div>;

		const items = this.groupProducts(model.items.slice());
		const view = (
			<List
				itemLayout="vertical"
				dataSource={items}
				renderItem={this.renderItem}
				split={false}
				className="search-result-list"
				loadMore={loadMore}
			/>
		);

		const points = items
			.reduce((prev: any[], curr) => prev.concat(curr.filter(x => x.product.location).map(x => ({ lat: x.product.location.latitude, lng: x.product.location.longitude, text: x.product.name }))), []);
		const mapComponent = points.length > 0
			? (
				<Collapse.Panel key="map" header="Map view">
					<div style={{ width: '100%', height: '50vh' }}>
						<MapView
							center={points}
							zoom={13}
						>
							{points.map(p => <MapMarker {...p} key={`${p.lat}, ${p.lng}`} />)}
						</MapView>
					</div>
				</Collapse.Panel>
			)
			: null;
		return (
			<React.Fragment>
				{headerView}
				<Collapse bordered={false} defaultActiveKey={['3']} >
					{headerMsg}
					{mapComponent}
					<Collapse.Panel key="3" header={`Results (${count})`}>
						{view}
					</Collapse.Panel>
				</Collapse>
			</React.Fragment>
		);
	}

	private onLoadMore = async () => {
		try {
			await this.props.onPagingSearch();
		} catch (err) {
			debugger;
		}
	}

	private onChangeSelection1 = (selectedRowKeys: string[] | number[], selectedRows: ResultOption[]) => {
		let selection = this.state.selection;
		if (selection == null) {
			selection = {};
		}
		const selected = selectedRows[0];
		selection[selected.unitIndex] = {
			selected: selected,
			supDisc: selected.result.subProducts
				.filter((e: SupplementSearchResult) => e.isRequired && e.costPrice[selected.unitIndex] && e.sellPrice[selected.unitIndex])//Parche porque pueden venir required sin cost o sell
				.map((e: SupplementSearchResult) => ({
					id: e.id,
					name: e.product.name,
					unitIndex: selected.unitIndex,
					cost: e.costPrice[selected.unitIndex],
					sell: e.sellPrice[selected.unitIndex],
					isRequired: e.isRequired,
					result: e
				})),
		};
		this.setState({
			selection: selectedRowKeys == null ? null : selection,
		});

		const { currentSearch, onProductSelected } = this.props;
		const unitCount = currentSearch.occupancies.map(e => e.unit);
		const products = [];
		for (const prop of Object.getOwnPropertyNames(selection)) {
			const unitIndex = parseInt(prop);
			products.push(selection[unitIndex]);
		}
		if (unitCount.length === products.length && onProductSelected) {
			onProductSelected(products, currentSearch);
		}
	}

	//este es el que funciona para todos menos Accommodation
	private onChangeSelection = (selectedRowKeys: string[] | number[], selectedRows: ResultOption[]) => {
		let selection = this.state.selection;
		if (selection == null) {
			selection = {};
		}
		for (const selected of selectedRows) {
			if (selection[selected.unitIndex] && selection[selected.unitIndex].selected.id === selected.id) {
				continue;
			}
			selection[selected.unitIndex] = {
				selected: selected,
				supDisc: selected.result.subProducts
					.filter((e: { isRequired: boolean }) => e.isRequired)
					.map((e: SupplementSearchResult) => ({
						id: e.id,
						name: e.product.name,
						unitIndex: selected.unitIndex,
						cost: e.costPrice[selected.unitIndex],
						sell: e.sellPrice[selected.unitIndex],
						isRequired: e.isRequired,
						result: e
					})),
			};
		}
		this.setState({
			selection: selectedRowKeys == null ? null : selection,
		});

		const { currentSearch, onProductSelected } = this.props;
		const unitCount = currentSearch.occupancies.map(e => e.unit);
		const products = [];
		for (const prop of Object.getOwnPropertyNames(selection)) {
			const unitIndex = parseInt(prop);
			products.push(selection[unitIndex]);
		}
		if (unitCount.length === products.length) {
			onProductSelected(products, currentSearch);
		}
	}

	private onChangeOptionalSelection = (selectedRowKeys: string[] | number[], selectedRows: ResultOption[]) => {
		let selection = { ...this.state.selection };
		
		for (const selected of selectedRows) {
			const result = selection[selected.unitIndex];
			result.supDisc.splice(0);
			
		}
		for (const selected of selectedRows) {
			const result = selection[selected.unitIndex];
			if (result == null || selected.parent.id !== result.selected.id) {
				continue;
			}
			result.supDisc.push(selected);
		}
		this.setState({
			selection: selection,
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/results/search-results.view.tsx